/**
 * CommunicationTab Component
 *
 * Displays email threads for a contact or company.
 * Reusable between contact and company detail views.
 */

import { useTranslation } from 'react-i18next';
import type { CommunicationResponse, EmailThread } from '../types';

interface CommunicationTabProps {
  data: CommunicationResponse | null;
  loading: boolean;
}

export default function CommunicationTab({ data, loading }: CommunicationTabProps) {
  const { t } = useTranslation('people');
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !data.threads || data.threads.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="font-medium">{t('communication_tab.empty_state.title')}</p>
        <p className="text-sm mt-1">{t('communication_tab.empty_state.description')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {t('communication_tab.email_threads')}
        </h4>
        <div className="mt-3 grid gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('communication_tab.total_threads')}</span>
            <span className="text-gray-900">{data.threads.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">{t('communication_tab.total_messages')}</span>
            <span className="text-gray-900">{data.threads.reduce((acc, t) => acc + (t.message_count || 0), 0)}</span>
          </div>
        </div>
      </div>

      {/* Thread List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {data.threads.map((thread) => (
            <ThreadRow key={thread.id} thread={thread} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Thread row component
function ThreadRow({ thread }: { thread: EmailThread }) {
  const { t } = useTranslation('people');
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return t('communication_tab.yesterday');
    } else if (diffDays < 7) {
      return date.toLocaleDateString(undefined, { weekday: 'short' });
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {thread.subject || t('communication_tab.no_subject')}
          </p>
          {thread.snippet && (
            <p className="text-sm text-gray-500 truncate mt-0.5">
              {thread.snippet}
            </p>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
            <span>{t('communication_tab.message_count', { count: thread.message_count })}</span>
          </div>
        </div>
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {thread.last_message_at ? formatDate(thread.last_message_at) : ''}
        </div>
      </div>
    </div>
  );
}
